import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { ProofUpload } from "../components/ProofUpload";
import { api, money, orderRef, PLATFORMS } from "../lib/api";

const STATUS = {
  awaiting_payment: ["Awaiting payment", "text-[#e9d5ff]"],
  paid: ["Paid — delivery in progress", "text-[#a259ff]"],
  delivered: ["Delivered", "text-emerald-300"],
  cancelled: ["Cancelled", "text-[#ff3dc8]"],
};

const CopyRow = ({ label, value, testId }) => (
  <div className="flex items-center justify-between gap-4 border border-white/10 bg-black/40 p-4">
    <div className="min-w-0">
      <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{label}</p>
      <p className="mt-1 break-all font-mono text-sm text-white">{value}</p>
    </div>
    <button
      onClick={() => navigator.clipboard?.writeText(value).then(() => toast.success("Copied"))}
      data-testid={testId}
      className="shrink-0 rounded-full border border-white/20 p-2.5 text-slate-200 transition-colors hover:border-[#a259ff] hover:text-[#a259ff]"
      aria-label={`Copy ${label}`}
    >
      <Copy className="h-4 w-4" />
    </button>
  </div>
);

const Instructions = ({ order, s }) => {
  const ref = orderRef(order.id);
  if (order.status !== "awaiting_payment") return null;
  if (order.payment_method === "cashapp")
    return (
      <div className="space-y-3">
        <p className="text-slate-300">Send <b className="text-white">{money(order.amount)}</b> on Cash App and put your reference in the note.</p>
        <CopyRow label="Cash App" value={s?.cashapp_tag ?? ""} testId="copy-cashapp" />
        <CopyRow label="Note / reference" value={ref} testId="copy-reference" />
      </div>
    );
  if (order.payment_method === "crypto")
    return (
      <div className="space-y-3">
        <p className="text-slate-300">Send the equivalent of <b className="text-white">{money(order.amount)}</b> to the wallet below, then message us your reference and transaction ID.</p>
        {[["Bitcoin (BTC)", s?.btc_address, "copy-btc"], ["Ethereum (ETH)", s?.eth_address, "copy-eth"], ["USDT (TRC20)", s?.usdt_address, "copy-usdt"]]
          .filter(([, v]) => v && !v.startsWith("["))
          .map(([label, v, id]) => <CopyRow key={id} label={label} value={v} testId={id} />)}
        <CopyRow label="Reference" value={ref} testId="copy-reference" />
      </div>
    );
  if (order.payment_method === "paypal")
    return (
      <div className="space-y-3">
        <p className="text-slate-300">Scan the QR code with your PayPal app and send <b className="text-white">{money(order.amount)}</b>. Put your reference in the note.</p>
        <div className="mx-auto w-full max-w-[300px] bg-white p-4" data-testid="paypal-qr">
          <img src={`${process.env.PUBLIC_URL}/brand/paypal-qr.png`} alt="OGM PayPal QR code" className="aspect-square w-full object-contain" />
        </div>
        {s?.paypal_handle && <CopyRow label="PayPal" value={s.paypal_handle} testId="copy-paypal" />}
        <CopyRow label="Note / reference" value={ref} testId="copy-reference" />
      </div>
    );
  if (order.payment_method === "applepay")
    return (
      <div className="space-y-3">
        <p className="text-slate-300">Send <b className="text-white">{money(order.amount)}</b> with Apple Cash (Messages or Wallet app) to the number below and put your reference in the message.</p>
        <CopyRow label="Apple Pay / Apple Cash number" value={s?.applepay_number ?? ""} testId="copy-applepay" />
        <CopyRow label="Message / reference" value={ref} testId="copy-reference" />
      </div>
    );
  if (order.payment_method === "manual")
    return <p className="text-slate-300">Got it. We'll message you on the handle you gave us to arrange payment. Keep your reference <b className="text-white">{ref}</b> handy.</p>;
  return <p className="text-slate-300">Your card payment hasn't been confirmed yet. If you closed the Stripe page, contact us with your reference.</p>;
};

export default function OrderPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const k = params.get("k") ?? "";
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/orders/${id}`, { params: { k } }).then((r) => setOrder(r.data ?? null)).catch(() => setError(true));
    api.get("/settings").then((r) => setSettings(r.data ?? null)).catch(() => setSettings(null));
  }, [id, k]);

  const [label, cls] = STATUS[order?.status] ?? ["", ""];

  return (
    <div className="grain grid-lines min-h-screen bg-[#07070b] px-5 py-8 md:px-10">
      <Link to="/" data-testid="order-home-link"><Logo /></Link>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="mx-auto mt-14 max-w-2xl border border-white/10 bg-[#0b0b11]/90 p-7 backdrop-blur md:p-10" data-testid="order-success-container">
        {error && <p className="text-slate-300" data-testid="order-not-found">We couldn't open that order. <Link to="/find-order" className="text-[#a259ff] underline">Get your order links by email</Link>.</p>}
        {!error && !order && <p className="font-mono text-sm uppercase tracking-widest text-slate-400">Loading order…</p>}
        {order && (
          <>
            <div className="flex items-center gap-3">
              {order.status === "awaiting_payment" ? <Clock className="h-6 w-6 text-[#e9d5ff]" /> : <CheckCircle2 className="h-6 w-6 text-[#a259ff]" />}
              <p className={`font-mono text-xs uppercase tracking-widest ${cls}`} data-testid="order-status">{label}</p>
            </div>
            <h1 className="mt-4 font-display text-6xl uppercase leading-none text-white md:text-7xl">Order locked in.</h1>
            <div className="mt-8 grid grid-cols-2 gap-4 border-y border-white/10 py-6 font-mono text-sm sm:grid-cols-3">
              <div><p className="text-[11px] uppercase tracking-widest text-slate-400">Reference</p><p className="mt-1 text-white" data-testid="order-reference">{orderRef(order.id)}</p></div>
              <div><p className="text-[11px] uppercase tracking-widest text-slate-400">Account</p><p className="mt-1 text-white">{order.product_name} · {PLATFORMS[order.platform]?.label}</p></div>
              <div><p className="text-[11px] uppercase tracking-widest text-slate-400">Total</p><p className="mt-1 text-[#e9d5ff]">{money(order.amount)}</p>{order.discount_code && <p className="text-[11px] text-slate-400">{order.discount_code} applied</p>}</div>
            </div>
            <div className="mt-8"><Instructions order={order} s={settings} /></div>
            {settings?.delivery_time && ["awaiting_payment", "paid"].includes(order.status) && (
              <p className="mt-6 font-mono text-xs uppercase tracking-widest text-slate-300" data-testid="order-delivery-time">Delivery after payment: <span className="text-white">{settings.delivery_time}</span></p>
            )}
            <ProofUpload order={order} k={k} onUploaded={(n) => setOrder((o) => ({ ...o, proof_count: n }))} />
            {order.status === "paid" && <p className="mt-6 text-slate-300">Payment received. We'll email you as soon as your account login is ready on this page.</p>}
            {order.status === "delivered" && order.delivery_details && (
              <div className="mt-8 border border-[#a259ff]/40 bg-[#a259ff]/5 p-5" data-testid="order-delivery-details">
                <p className="font-mono text-[11px] uppercase tracking-widest text-violet-300">Your account login</p>
                <pre className="mt-3 whitespace-pre-wrap break-all font-mono text-sm text-white">{order.delivery_details}</pre>
                <button onClick={() => navigator.clipboard?.writeText(order.delivery_details).then(() => toast.success("Copied"))} data-testid="copy-delivery-details" className="mt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-violet-300 hover:text-white">
                  <Copy className="h-4 w-4" /> Copy details
                </button>
                <p className="mt-4 text-xs text-slate-400">Change the email and password on the account as soon as you log in. Don't share this page link.</p>
                <Link to={`/review/${order.id}?k=${encodeURIComponent(k)}`} className="mt-5 inline-block bg-[#ff3dc8] px-5 py-3 font-mono text-xs font-bold uppercase tracking-widest text-white" data-testid="order-leave-review-link">Leave a review</Link>
              </div>
            )}
            <p className="mt-8 text-sm text-slate-400">Bookmark this page to check your order status.</p>
          </>
        )}
      </motion.div>
    </div>
  );
}
